import { Injectable } from '@angular/core';

interface DirtyStore {
  [apiKey: string]: string[];
}

interface TimestampStore {
  [apiKey: string]: number;
}

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private readonly DIRTY_STORE_KEY = 'apiCacheDirtyStore';
  private readonly TIMESTAMP_STORE_KEY = 'apiCacheTimestamps';
  private readonly CACHE_MAX_AGE = 300000; // 5 mins in ms
  private isPageReload = true;
  public forceMemberRefresh = false;
  public dashbaordPageReload = true;
  public taskPageReload = true;
  public memberModalPageReload = true;
  public surveyListingPageReload = true;
  public dashboardPreviousQuarterReload = true;
  public memberListingPageReload = true;
  public addMemberPageReload = true;

  public readonly API_KEYS = {
    MEMBERS_LISTING: 'membersListing',
    DASHBOARD_LISTING: 'dashboardListing',
    MAPPED_SHG_LISTING: 'mappedShgListing',
    MEMBER_MODAL_LISTING: 'memberModalListing',
    SURVEY_LISTING: 'surveyListing',
    DASHBOARD_COUNT: 'dashboardCount',
    INACTIVE_MEMBER_LISTING: 'inactiveMemberListing',
  };

  setPageReload(value: boolean): void {
    this.isPageReload = value;
    this.dashbaordPageReload = value;
    this.taskPageReload = value;
    this.surveyListingPageReload = value;
    this.dashboardPreviousQuarterReload = value;
    this.memberListingPageReload = value;
    this.addMemberPageReload = value;
  }

  /**
   * Get the ts param to use for an API call.
   * - If dirty ids exist, generate new timestamp (force refresh).
   * - If a valid (non-expired) timestamp exists, reuse it (browser serves cached response).
   * - Otherwise, generate a new timestamp.
   */
  getTimestamp(apiKey: string): number | null {
    if (
      this.isPageReload ||
      (apiKey === this.API_KEYS.MEMBERS_LISTING && this.forceMemberRefresh) ||
      (apiKey === this.API_KEYS.DASHBOARD_LISTING &&
        this.dashbaordPageReload) ||
      (apiKey === this.API_KEYS.MAPPED_SHG_LISTING && this.taskPageReload) ||
      (apiKey === this.API_KEYS.MEMBER_MODAL_LISTING &&
        this.memberModalPageReload) ||
      (apiKey === this.API_KEYS.SURVEY_LISTING &&
        this.surveyListingPageReload) ||
      (apiKey === this.API_KEYS.DASHBOARD_COUNT &&
        this.dashboardPreviousQuarterReload) ||
      (apiKey === this.API_KEYS.MEMBERS_LISTING &&
        this.memberListingPageReload) ||
      (apiKey === this.API_KEYS.INACTIVE_MEMBER_LISTING &&
        this.addMemberPageReload)
    ) {
      if (this.isPageReload) this.isPageReload = false;
      const date = Date.now();
      this.setTimestamp(apiKey, date);
      this.clearDirtyIds(apiKey);
      this.forceMemberRefresh = false;
      if (apiKey === this.API_KEYS.MAPPED_SHG_LISTING) {
        this.taskPageReload = false;
      }
      if (apiKey === this.API_KEYS.DASHBOARD_LISTING) {
        this.dashbaordPageReload = false;
      }
      if (apiKey === this.API_KEYS.MEMBER_MODAL_LISTING) {
        this.memberModalPageReload = false;
      }
      if (apiKey === this.API_KEYS.SURVEY_LISTING) {
        this.surveyListingPageReload = false;
      }
      if (apiKey === this.API_KEYS.DASHBOARD_COUNT) {
        this.dashboardPreviousQuarterReload = false;
      }
      if (apiKey === this.API_KEYS.MEMBERS_LISTING) {
        this.memberListingPageReload = false;
      }
      if (apiKey === this.API_KEYS.INACTIVE_MEMBER_LISTING) {
        this.addMemberPageReload = false;
      }
      return date;
    }
    const store = this.getTimestampStore();
    const cached = store[apiKey];
    if (!cached) {
      return null;
    }

    if (cached && Date.now() - cached < this.CACHE_MAX_AGE) {
      return cached; // reuse same ts so browser serves cached response
    }
    this.clearTimestamp(apiKey); // expired timestamp, clear it

    const timestamp = Date.now();
    this.setTimestamp(apiKey, timestamp);
    return timestamp; // expired or no timestamp, new fetch
  }

  /**
   * Store the timestamp after a successful API response
   */
  setTimestamp(apiKey: string, ts: number): void {
    const store = this.getTimestampStore();
    store[apiKey] = ts;
    localStorage.setItem(this.TIMESTAMP_STORE_KEY, JSON.stringify(store));
  }

  /**
   * Clear timestamp for an API key
   */
  clearTimestamp(apiKey: string): void {
    const store = this.getTimestampStore();
    delete store[apiKey];
    localStorage.setItem(this.TIMESTAMP_STORE_KEY, JSON.stringify(store));
  }

  markDirty(apiKey: string, id: string | number): void {
    const store = this.getStore();
    if (!store[apiKey]) {
      store[apiKey] = [];
    }
    if (!store[apiKey].includes(id.toString())) {
      store[apiKey].push(id.toString());
    }
    this.saveStore(store);
  }

  hasDirtyIds(apiKey: string): boolean {
    const store = this.getStore();
    return (store[apiKey] || []).length > 0;
  }

  getDirtyIds(apiKey: string): string[] {
    const store = this.getStore();
    return store[apiKey] || [];
  }
  isIdDirty(apiKey: string, id: string | number): boolean {
    const store = this.getStore();
    return (store[apiKey] || []).includes(id.toString());
  }

  clearDirtyIds(apiKey: string): void {
    const store = this.getStore();
    delete store[apiKey];
    this.saveStore(store);
  }

  clearAll(): void {
    localStorage.removeItem(this.DIRTY_STORE_KEY);
    localStorage.removeItem(this.TIMESTAMP_STORE_KEY);
  }

  private getStore(): DirtyStore {
    const raw = localStorage.getItem(this.DIRTY_STORE_KEY);
    return raw ? JSON.parse(raw) : {};
  }

  private saveStore(store: DirtyStore): void {
    localStorage.setItem(this.DIRTY_STORE_KEY, JSON.stringify(store));
  }

  private getTimestampStore(): TimestampStore {
    const raw = localStorage.getItem(this.TIMESTAMP_STORE_KEY);
    return raw ? JSON.parse(raw) : {};
  }
}
